/**
 * RF-11.3 · Trazabilidad global de acciones
 * (`/api/trazabilidad`).
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, getAuthToken } from '@/services/http'

const API_BASE = process.env.VUE_APP_API_BASE_URL ?? ''
const BASE = '/api/auditoria'

/** Forma con la que el API de trazabilidad devuelve este dato. */
export interface RegistroAuditoria {
  id: number
  id_usuario: number | null
  accion: string
  entidad: string
  entidad_id: number | null
  id_ejecucion: number | null
  descripcion: string | null
  valores_anteriores: Record<string, unknown> | null
  valores_nuevos: Record<string, unknown> | null
  ip_address: string | null
  created_at: string
  usuario?: {
    id: number
    name: string
    email: string
    roles?: { id: number; name: string; label?: string | null }[]
  } | null
}

/** Forma con la que el API de trazabilidad devuelve este dato. */
export interface FiltrosAuditoria {
  id_usuario?: number | string
  id_ejecucion?: number | string
  entidad?: string
  accion?: string
  fecha_desde?: string
  fecha_hasta?: string
  q?: string
  per_page?: number
  page?: number
}

/** Forma con la que el API de trazabilidad devuelve este dato. */
export interface Paginado<T> {
  data: T[]
  current_page: number
  last_page: number
  total: number
  per_page: number
}

/** Forma con la que el API de trazabilidad devuelve este dato. */
export interface OpcionesFiltro {
  entidades: string[]
  acciones: string[]
  usuarios: { id: number; name: string; email: string }[]
}

function queryString(filtros: Record<string, unknown>): string {
  const params = new URLSearchParams()
  Object.entries(filtros).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.append(k, String(v))
  })
  const s = params.toString()
  return s ? `?${s}` : ''
}

/**
 * Lista trazabilidad · petición `GET`.
 */
export function listarAuditoria(filtros: FiltrosAuditoria = {}) {
  return http.get<Paginado<RegistroAuditoria>>(`${BASE}${queryString(filtros as Record<string, unknown>)}`)
}

/**
 * Operación sobre trazabilidad · petición `GET`.
 */
export function opcionesAuditoria() {
  return http.get<OpcionesFiltro>(`${BASE}/filtros`)
}

/** Descarga el registro filtrado como CSV (o JSON) usando el token de sesión. */
export async function exportarAuditoria(filtros: FiltrosAuditoria = {}, formato: 'csv' | 'json' = 'csv'): Promise<boolean> {
  const url = `${API_BASE}${BASE}/exportar${queryString({ ...filtros, formato })}`
  const token = getAuthToken()

  const resp = await fetch(url, {
    headers: { Accept: '*/*', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  })
  if (!resp.ok) return false

  const blob = await resp.blob()
  const enlace = document.createElement('a')
  enlace.href = URL.createObjectURL(blob)
  enlace.download = `trazabilidad_${new Date().toISOString().slice(0, 10)}.${formato === 'json' ? 'json' : 'csv'}`
  document.body.appendChild(enlace)
  enlace.click()
  enlace.remove()
  URL.revokeObjectURL(enlace.href)
  return true
}
